import { useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  AppBar,
  Avatar,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import DashboardIcon from "@mui/icons-material/Dashboard";
import AppsIcon from "@mui/icons-material/Apps";
import GroupsIcon from "@mui/icons-material/Groups";
import PeopleIcon from "@mui/icons-material/People";
import SettingsIcon from "@mui/icons-material/Settings";
import ArticleIcon from "@mui/icons-material/Article";
import TuneIcon from "@mui/icons-material/Tune";
import MenuIcon from "@mui/icons-material/Menu";
import { useAuth } from "../../hooks/useAuth";
import { useTenant } from "../../hooks/useTenant";

const DRAWER_WIDTH = 240;

export default function AppLayout() {
  const { t } = useTranslation();

  const navItems = [
    { label: t("nav.dashboard"), path: "/app", icon: <DashboardIcon /> },
    { label: t("nav.applications"), path: "/app/applications", icon: <AppsIcon /> },
    { label: t("nav.organisations"), path: "/app/organisations", icon: <GroupsIcon /> },
    { label: t("nav.users"), path: "/app/users", icon: <PeopleIcon /> },
    { label: t("nav.roles"), path: "/app/roles", icon: <TuneIcon /> },
    { label: t("nav.logs", "Logs"), path: "/app/logs", icon: <ArticleIcon /> },
    { label: t("nav.settings"), path: "/app/settings", icon: <SettingsIcon /> },
  ];
  const { user, logout } = useAuth();
  const { activeTenant, tenants, setActiveTenant } = useTenant();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [tenantMenuAnchor, setTenantMenuAnchor] = useState<null | HTMLElement>(null);

  const drawerContent = (
    <Box>
      <Toolbar>
        <Typography variant="h6" noWrap>
          Janus
        </Typography>
      </Toolbar>
      <Divider />
      <Box
        sx={{ px: 2, py: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between", cursor: tenants.length > 1 ? "pointer" : "default" }}
        onClick={(e) => tenants.length > 1 && setTenantMenuAnchor(e.currentTarget)}
      >
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
            Workspace
          </Typography>
          <Typography variant="body2">{activeTenant?.name ?? "No workspace"}</Typography>
        </Box>
        {tenants.length > 1 && (
          <Tooltip title="Switch workspace">
            <SwapHorizIcon fontSize="small" color="action" />
          </Tooltip>
        )}
      </Box>
      <Menu
        anchorEl={tenantMenuAnchor}
        open={Boolean(tenantMenuAnchor)}
        onClose={() => setTenantMenuAnchor(null)}
      >
        {tenants.map((t) => (
          <MenuItem
            key={t.id}
            selected={t.id === activeTenant?.id}
            onClick={() => {
              setActiveTenant(t);
              setTenantMenuAnchor(null);
            }}
          >
            {t.name}
          </MenuItem>
        ))}
      </Menu>
      <Divider />
      <List>
        {navItems.map((item) => (
          <ListItemButton
            key={item.path}
            selected={location.pathname === item.path}
            onClick={() => {
              navigate(item.path);
              setMobileOpen(false);
            }}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText primary={item.label} />
          </ListItemButton>
        ))}
      </List>
    </Box>
  );

  const initials = user ? `${user.first_name?.[0] ?? ""}${user.last_name?.[0] ?? ""}`.toUpperCase() : "?";

  const avatarColor = (() => {
    const colors = ["#5C6BC0", "#26A69A", "#EF5350", "#AB47BC", "#42A5F5", "#FF7043", "#66BB6A", "#EC407A"];
    const str = `${user?.first_name ?? ""}${user?.last_name ?? ""}`;
    const index = [...str].reduce((acc, ch) => acc + ch.charCodeAt(0), 0) % colors.length;
    return colors[index];
  })();

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={() => setMobileOpen(!mobileOpen)}
            sx={{ mr: 2, display: { sm: "none" } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap sx={{ flexGrow: 1 }}>
            Janus Console
          </Typography>
          <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ ml: 1 }}>
            <Avatar
              src={user?.avatar ?? undefined}
              sx={{ width: 34, height: 34, bgcolor: avatarColor, fontSize: 13, fontWeight: 600, letterSpacing: 0.5 }}
            >
              {initials}
            </Avatar>
          </IconButton>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
            <MenuItem disabled>
              <Typography variant="body2">{user?.email}</Typography>
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                logout();
              }}
            >
              Sign out
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        sx={{
          display: { xs: "block", sm: "none" },
          "& .MuiDrawer-paper": { width: DRAWER_WIDTH },
        }}
      >
        {drawerContent}
      </Drawer>

      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", sm: "block" },
          width: DRAWER_WIDTH,
          flexShrink: 0,
          "& .MuiDrawer-paper": { width: DRAWER_WIDTH, boxSizing: "border-box" },
        }}
        open
      >
        {drawerContent}
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, p: 3, ml: { sm: 0 }, width: { sm: `calc(100% - ${DRAWER_WIDTH}px)` } }}>
        <Toolbar />
        <Box sx={{ maxWidth: 1100, mx: "auto" }}>
          <Outlet key={activeTenant?.id ?? "none"} />
        </Box>
      </Box>
    </Box>
  );
}
